// src/components/richtext/RichTextEditorTipTap.jsx
import React, {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react'
import { useEditor, useEditorState, EditorContent } from '@tiptap/react'
import {
  LuBold, LuItalic, LuUnderline, LuStrikethrough,
  LuList, LuListOrdered,
  LuHeading1, LuHeading2, LuHeading3,
  LuAlignLeft, LuAlignCenter, LuAlignRight, LuAlignJustify,
  LuLink, LuImagePlus,
  LuQuote, LuCode, LuMinus, LuEraser,
  LuUndo, LuRedo, LuEllipsis,
} from 'react-icons/lu'
import { sanitizeHtml } from '../../lib/richtext/htmlSanitizer'
import { toPlainTextFromHtml, ensureTipTapFormat } from '../../lib/richtext/tiptapConverters'
import { buildRichTextExtensions } from '../../lib/richtext/tiptapExtensions'
import useIsTouch from '../../lib/richtext/useIsTouch'
import '../../styles/tiptap.css'
import Tooltip from '../common/Tooltip'
import LinkModal from './LinkModal'
import ColorSwatch from './ColorSwatch'
import EditorErrorBoundary from './EditorErrorBoundary'
import MobileFormatSheet from './MobileFormatSheet'
import apiService from '../../services/api'

const ONCHANGE_DEBOUNCE_MS = 200
const IMAGE_MIME = /^image\//
const isModKey = (e) => e.metaKey || e.ctrlKey

const sameJson = (a, b) => {
  try { return JSON.stringify(a) === JSON.stringify(b) }
  catch { return false }
}

const formatCount = (n) => new Intl.NumberFormat().format(n)

// `useEditorState` returns null for one render between editor creation and the
// store sync. We use this as a fallback so the editor renders immediately and
// the auto-resize effect can run with a mounted container ref.
const EMPTY_STATE = {
  bold: false, italic: false, underline: false, strike: false,
  bulletList: false, orderedList: false,
  h1: false, h2: false, h3: false,
  alignLeft: false, alignCenter: false, alignRight: false, alignJustify: false,
  link: false, blockquote: false, codeBlock: false,
  color: '', highlight: '', linkHref: '',
  canUndo: false, canRedo: false,
  characters: 0, words: 0,
}

// Tooltip text — drop the (Ctrl+X) hint for touch users since they don't have one.
const tip = (label, shortcut, isTouch) =>
  isTouch || !shortcut ? label : `${label} (${shortcut})`

function RichTextEditorTipTapInner({
  value,
  onChange,
  placeholder = 'Write something…',
  readOnly = false,
  error = false,
  disabled = false,
  autoResize = true,
  pasteAsPlainText = false,
  enableImages = false,
  characterLimit,
  showCharacterCount = false,
  saveState,
  className = '',
}) {
  const containerRef = useRef(null)
  const editorRef = useRef(null)
  const toolbarRef = useRef(null)
  const imageInputRef = useRef(null)
  const moreBtnRef = useRef(null)

  const isTouch = useIsTouch()
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkModalInitialUrl, setLinkModalInitialUrl] = useState('')
  const [moreSheetOpen, setMoreSheetOpen] = useState(false)
  const savedSelectionRef = useRef(null)

  const extensions = useMemo(() => {
    return buildRichTextExtensions({
      placeholder,
      characterLimit,
      withImage: enableImages,
      extra: [],
    })
  }, [placeholder, characterLimit, enableImages])

  // Debounced onChange.
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])
  const flushTimerRef = useRef(null)
  const pendingPayloadRef = useRef(null)
  const flushPending = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current)
      flushTimerRef.current = null
    }
    const payload = pendingPayloadRef.current
    pendingPayloadRef.current = null
    if (payload && onChangeRef.current) {
      try { onChangeRef.current(payload) }
      catch (err) { console.error('Error calling onChange:', err) }
    }
  }, [])

  const notifyChangeNow = useCallback((ed = editorRef.current) => {
    if (!ed || !onChangeRef.current) return
    const html = sanitizeHtml(ed.getHTML())
    const payload = {
      content_json: ed.getJSON(),
      content_html: html,
      content_plain: toPlainTextFromHtml(html),
    }
    pendingPayloadRef.current = payload
    flushPending()
  }, [flushPending])

  const uploadImageFile = useCallback(async (file) => {
    try {
      const res = await apiService.uploadFile(file, 'richtext')
      return res?.file?.url || null
    } catch (err) {
      console.error('Image upload failed:', err?.data?.message || err?.message || err)
      return null
    }
  }, [])

  const editor = useEditor({
    extensions,
    editable: !readOnly && !disabled,
    immediatelyRender: false,
    content: (() => {
      const normalized = ensureTipTapFormat(value)
      return normalized.content_json || normalized.content_html || ''
    })(),
    onUpdate: ({ editor }) => {
      if (!onChangeRef.current) return
      const json = editor.getJSON()
      const html = sanitizeHtml(editor.getHTML())
      const text = toPlainTextFromHtml(html)
      pendingPayloadRef.current = { content_json: json, content_html: html, content_plain: text }
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
      flushTimerRef.current = setTimeout(flushPending, ONCHANGE_DEBOUNCE_MS)
    },
    onBlur: () => flushPending(),
    editorProps: {
      attributes: {
        'aria-label': 'Rich text editor',
        class: 'tiptap-editor',
      },
      transformPastedHTML: (html) => sanitizeHtml(html),
      handleKeyDown: (view, event) => {
        if (isModKey(event) && !event.shiftKey && (event.key === 'k' || event.key === 'K')) {
          event.preventDefault()
          openLinkModal()
          return true
        }
        return false
      },
      handlePaste: (view, event) => {
        if (enableImages) {
          const files = Array.from(event.clipboardData?.files || [])
            .filter(f => IMAGE_MIME.test(f.type))
          if (files.length) {
            event.preventDefault()
            insertImageFiles(files)
            return true
          }
        }
        const wantsPlain = pasteAsPlainText
          || (event instanceof ClipboardEvent && event.shiftKey)
        if (wantsPlain) {
          const text = event.clipboardData?.getData('text/plain')
          if (text) {
            event.preventDefault()
            view.dispatch(view.state.tr.insertText(text))
            return true
          }
        }
        return false
      },
      handleDrop: (view, event) => {
        if (!enableImages) return false
        const files = Array.from(event.dataTransfer?.files || [])
          .filter(f => IMAGE_MIME.test(f.type))
        if (!files.length) return false
        event.preventDefault()
        insertImageFiles(files)
        return true
      },
    },
  }, [extensions])

  editorRef.current = editor

  const insertImageFiles = useCallback(async (files) => {
    if (!editorRef.current) return
    for (const file of files) {
      const url = await uploadImageFile(file)
      if (url) editorRef.current.chain().focus().setImage({ src: url, alt: file.name }).run()
    }
  }, [uploadImageFile])

  const openImagePicker = useCallback(() => imageInputRef.current?.click(), [])
  const onImageInputChange = useCallback((e) => {
    const files = Array.from(e.target.files || []).filter(f => IMAGE_MIME.test(f.type))
    if (files.length) insertImageFiles(files)
    e.target.value = '' // allow re-picking the same file
  }, [insertImageFiles])

  const saveSelection = useCallback(() => {
    if (editorRef.current) savedSelectionRef.current = editorRef.current.state.selection
  }, [])
  const restoreSelection = useCallback(() => {
    const sel = savedSelectionRef.current
    if (!editorRef.current || !sel) return
    try {
      editorRef.current.chain().focus().setTextSelection({ from: sel.from, to: sel.to }).run()
    } catch { /* selection may be invalid after content edits */ }
    savedSelectionRef.current = null
  }, [])

  const openLinkModal = useCallback(() => {
    if (!editorRef.current) return
    saveSelection()
    setLinkModalInitialUrl(editorRef.current.getAttributes('link').href || '')
    setLinkModalOpen(true)
  }, [saveSelection])

  useEffect(() => () => flushPending(), [flushPending])

  // External value sync — preserves scroll across content swaps.
  useEffect(() => {
    if (!editor) return
    const normalized = ensureTipTapFormat(value)
    const proseEl = containerRef.current?.querySelector('.ProseMirror')
    const savedScroll = proseEl?.scrollTop ?? 0
    if (normalized.content_json) {
      if (sameJson(editor.getJSON(), normalized.content_json)) return
      editor.commands.setContent(normalized.content_json, false)
    } else if (normalized.content_html) {
      if (editor.getHTML() === normalized.content_html) return
      editor.commands.setContent(normalized.content_html, false)
    }
    requestAnimationFrame(() => {
      const el = containerRef.current?.querySelector('.ProseMirror')
      if (el) el.scrollTop = savedScroll
    })
  }, [value, editor])

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly && !disabled)
  }, [readOnly, disabled, editor])

  // iOS Safari paint nudge — when the editor card lives inside a wrapper with
  // `overflow:hidden + border-radius` (e.g. AssignmentSubmissionInline), Safari
  // can defer the initial paint of the toolbar layer until first user touch.
  // Reading `offsetHeight` after mount forces a synchronous layout/paint flush.
  useEffect(() => {
    if (!editor || !toolbarRef.current) return
    const flush = () => {
      const el = toolbarRef.current
      if (!el) return
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight
    }
    requestAnimationFrame(flush)
    const t1 = setTimeout(flush, 100)
    const t2 = setTimeout(flush, 400)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [editor])

  useEffect(() => {
    if (!editor || !containerRef.current || !autoResize) return
    const apply = () => {
      const el = containerRef.current?.querySelector('.ProseMirror')
      if (!el) return
      el.style.height = 'auto'
      const min = 120
      const max = window.innerWidth <= 640
        ? Math.min(window.innerHeight * 0.6, 500)
        : window.innerWidth <= 1024 ? 350 : 500
      el.style.height = `${Math.min(Math.max(el.scrollHeight, min), max)}px`
    }
    editor.on('update', apply)
    editor.on('create', apply)
    const initial = setTimeout(apply, 100)
    return () => {
      clearTimeout(initial)
      editor.off('update', apply)
      editor.off('create', apply)
    }
  }, [editor, autoResize])

  const rawState = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      // `useEditor({ immediatelyRender: false })` returns the editor object before
      // its ProseMirror view exists. Calling .can()/.isActive() at that point
      // throws, so bail out until the view is ready.
      if (!ed || !ed.view || ed.isDestroyed) return null
      const cc = ed.storage.characterCount
      let canUndo = false
      let canRedo = false
      try {
        canUndo = ed.can().undo()
        canRedo = ed.can().redo()
      } catch { /* view torn down mid-update */ }
      return {
        bold: ed.isActive('bold'),
        italic: ed.isActive('italic'),
        underline: ed.isActive('underline'),
        strike: ed.isActive('strike'),
        bulletList: ed.isActive('bulletList'),
        orderedList: ed.isActive('orderedList'),
        h1: ed.isActive('heading', { level: 1 }),
        h2: ed.isActive('heading', { level: 2 }),
        h3: ed.isActive('heading', { level: 3 }),
        alignLeft: ed.isActive({ textAlign: 'left' }),
        alignCenter: ed.isActive({ textAlign: 'center' }),
        alignRight: ed.isActive({ textAlign: 'right' }),
        alignJustify: ed.isActive({ textAlign: 'justify' }),
        link: ed.isActive('link'),
        blockquote: ed.isActive('blockquote'),
        codeBlock: ed.isActive('codeBlock'),
        color: ed.getAttributes('textStyle').color || '',
        highlight: ed.getAttributes('highlight').color || '',
        linkHref: ed.getAttributes('link').href || '',
        canUndo,
        canRedo,
        characters: cc?.characters?.() ?? 0,
        words: cc?.words?.() ?? 0,
      }
    },
  })

  const handleContainerClick = useCallback((e) => {
    if (!editor) return
    if (e.target.closest('.ProseMirror')) return
    if (e.target.closest('.rte-tt-toolbar')) return
    if (e.target.closest('.rte-tt-overlay')) return
    if (e.target.closest('.rte-tt-status')) return
    editor.chain().focus('end').run()
  }, [editor])

  const state = rawState || EMPTY_STATE

  // More popover / sheet contents.
  const Icon = (Cmp) => <Cmp className="rte-popover-svg" />
  const sheetSections = rawState ? [
    {
      title: 'Headings',
      items: [
        { label: 'Heading 1', icon: Icon(LuHeading1), active: state.h1,
          onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
        { label: 'Heading 2', icon: Icon(LuHeading2), active: state.h2,
          onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
        { label: 'Heading 3', icon: Icon(LuHeading3), active: state.h3,
          onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
      ],
    },
    {
      title: 'Alignment',
      items: [
        { label: 'Left', icon: Icon(LuAlignLeft), active: state.alignLeft,
          onClick: () => editor.chain().focus().setTextAlign('left').run() },
        { label: 'Center', icon: Icon(LuAlignCenter), active: state.alignCenter,
          onClick: () => editor.chain().focus().setTextAlign('center').run() },
        { label: 'Right', icon: Icon(LuAlignRight), active: state.alignRight,
          onClick: () => editor.chain().focus().setTextAlign('right').run() },
        { label: 'Justify', icon: Icon(LuAlignJustify), active: state.alignJustify,
          onClick: () => editor.chain().focus().setTextAlign('justify').run() },
      ],
    },
    {
      title: 'Style',
      items: [
        { label: 'Strike', icon: Icon(LuStrikethrough), active: state.strike,
          onClick: () => editor.chain().focus().toggleStrike().run() },
        { label: 'Quote', icon: Icon(LuQuote), active: state.blockquote,
          onClick: () => editor.chain().focus().toggleBlockquote().run() },
        { label: 'Code', icon: Icon(LuCode), active: state.codeBlock,
          onClick: () => editor.chain().focus().toggleCodeBlock().run() },
        { label: 'Divider', icon: Icon(LuMinus), active: false,
          onClick: () => editor.chain().focus().setHorizontalRule().run() },
        { label: 'Clear', icon: Icon(LuEraser), active: false,
          onClick: () => editor.chain().focus().unsetAllMarks().clearNodes().run() },
      ],
    },
    {
      title: 'History',
      items: [
        { label: 'Undo', icon: Icon(LuUndo), disabled: !state.canUndo,
          onClick: () => editor.chain().focus().undo().run() },
        { label: 'Redo', icon: Icon(LuRedo), disabled: !state.canRedo,
          onClick: () => editor.chain().focus().redo().run() },
      ],
    },
  ] : []

  if (!editor) {
    return (
      <div className={`rte-tt-container rte-tt-loading ${className}`} aria-busy="true">
        <div className="rte-tt-skeleton-toolbar" />
        <div className="rte-tt-skeleton-body" />
      </div>
    )
  }

  const cmd = (fn) => () => fn(editor.chain().focus()).run()
  const overLimit = characterLimit && state.characters > characterLimit
  const showStatusBar = (showCharacterCount || !!saveState)

  return (
    <div
      className={`rte-tt-container ${error ? 'error' : ''} ${className}`}
      ref={containerRef}
      onClick={handleContainerClick}
    >
      {!readOnly && !disabled && (
        <div className="rte-tt-toolbar" role="toolbar" aria-label="Formatting toolbar" ref={toolbarRef}>
          {/* Primary group — always visible on mobile */}
          <Tooltip content={tip('Bold', 'Ctrl+B', isTouch)} className="rte-primary">
            <button type="button" aria-label="Bold" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleBold())}
              aria-pressed={state.bold}
              className={`rte-btn rte-primary ${state.bold ? 'is-active' : ''}`}><LuBold className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Italic', 'Ctrl+I', isTouch)} className="rte-primary">
            <button type="button" aria-label="Italic" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleItalic())}
              aria-pressed={state.italic}
              className={`rte-btn rte-primary ${state.italic ? 'is-active' : ''}`}><LuItalic className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Underline', 'Ctrl+U', isTouch)} className="rte-primary">
            <button type="button" aria-label="Underline" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleUnderline())}
              aria-pressed={state.underline}
              className={`rte-btn rte-primary ${state.underline ? 'is-active' : ''}`}><LuUnderline className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Strikethrough', 'Ctrl+Shift+X', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Strikethrough" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleStrike())}
              aria-pressed={state.strike}
              className={`rte-btn rte-secondary ${state.strike ? 'is-active' : ''}`}><LuStrikethrough className="rte-icon" /></button>
          </Tooltip>

          <span className="rte-sep rte-secondary" />

          <Tooltip content={tip('Bullet List', 'Ctrl+Shift+8', isTouch)} className="rte-primary rte-primary-list">
            <button type="button" aria-label="Bullet list" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleBulletList())}
              aria-pressed={state.bulletList}
              className={`rte-btn rte-primary rte-primary-list ${state.bulletList ? 'is-active' : ''}`}><LuList className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Numbered List', 'Ctrl+Shift+7', isTouch)} className="rte-primary rte-primary-list">
            <button type="button" aria-label="Numbered list" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleOrderedList())}
              aria-pressed={state.orderedList}
              className={`rte-btn rte-primary rte-primary-list ${state.orderedList ? 'is-active' : ''}`}><LuListOrdered className="rte-icon" /></button>
          </Tooltip>

          <span className="rte-sep rte-secondary" />

          <Tooltip content={tip('Heading 1', 'Ctrl+Alt+1', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Heading 1" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleHeading({ level: 1 }))}
              aria-pressed={state.h1}
              className={`rte-btn rte-secondary ${state.h1 ? 'is-active' : ''}`}><LuHeading1 className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Heading 2', 'Ctrl+Alt+2', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Heading 2" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleHeading({ level: 2 }))}
              aria-pressed={state.h2}
              className={`rte-btn rte-secondary ${state.h2 ? 'is-active' : ''}`}><LuHeading2 className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Heading 3', 'Ctrl+Alt+3', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Heading 3" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleHeading({ level: 3 }))}
              aria-pressed={state.h3}
              className={`rte-btn rte-secondary ${state.h3 ? 'is-active' : ''}`}><LuHeading3 className="rte-icon" /></button>
          </Tooltip>

          <span className="rte-sep rte-secondary" />

          <Tooltip content={tip('Align Left', 'Ctrl+Shift+L', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Align left" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.setTextAlign('left'))}
              aria-pressed={state.alignLeft}
              className={`rte-btn rte-secondary ${state.alignLeft ? 'is-active' : ''}`}><LuAlignLeft className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Align Center', 'Ctrl+Shift+E', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Align center" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.setTextAlign('center'))}
              aria-pressed={state.alignCenter}
              className={`rte-btn rte-secondary ${state.alignCenter ? 'is-active' : ''}`}><LuAlignCenter className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Align Right', 'Ctrl+Shift+R', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Align right" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.setTextAlign('right'))}
              aria-pressed={state.alignRight}
              className={`rte-btn rte-secondary ${state.alignRight ? 'is-active' : ''}`}><LuAlignRight className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Justify', 'Ctrl+Shift+J', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Justify" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.setTextAlign('justify'))}
              aria-pressed={state.alignJustify}
              className={`rte-btn rte-secondary ${state.alignJustify ? 'is-active' : ''}`}><LuAlignJustify className="rte-icon" /></button>
          </Tooltip>

          <span className="rte-sep rte-secondary" />

          <Tooltip content={tip(state.link ? 'Edit Link' : 'Add Link', 'Ctrl+K', isTouch)} className="rte-primary rte-primary-link">
            <button type="button" aria-label={state.link ? 'Edit link' : 'Add link'}
              onMouseDown={(e) => e.preventDefault()}
              onClick={openLinkModal}
              aria-pressed={state.link}
              className={`rte-btn rte-primary rte-primary-link ${state.link ? 'is-active' : ''}`}><LuLink className="rte-icon" /></button>
          </Tooltip>

          {enableImages && (
            <Tooltip content="Insert Image" className="rte-primary rte-primary-image">
              <button type="button" aria-label="Insert image"
                onMouseDown={(e) => e.preventDefault()}
                onClick={openImagePicker}
                className="rte-btn rte-primary rte-primary-image"><LuImagePlus className="rte-icon" /></button>
            </Tooltip>
          )}

          <span className="rte-sep rte-secondary" />

          <span className="rte-secondary">
            <Tooltip content="Text Color">
              <span>
                <ColorSwatch
                  kind="text"
                  label="Text color"
                  current={state.color}
                  onChange={(c) => editor.chain().focus().setColor(c).run()}
                  onUnset={() => editor.chain().focus().unsetColor().run()}
                />
              </span>
            </Tooltip>
          </span>

          <span className="rte-secondary">
            <Tooltip content="Highlight">
              <span>
                <ColorSwatch
                  kind="highlight"
                  label="Highlight color"
                  current={state.highlight}
                  onChange={(c) => editor.chain().focus().setHighlight({ color: c }).run()}
                  onUnset={() => editor.chain().focus().unsetHighlight().run()}
                />
              </span>
            </Tooltip>
          </span>

          <span className="rte-sep rte-secondary" />

          <Tooltip content={tip('Quote', 'Ctrl+Shift+B', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Blockquote" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleBlockquote())}
              aria-pressed={state.blockquote}
              className={`rte-btn rte-secondary ${state.blockquote ? 'is-active' : ''}`}><LuQuote className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Code Block', 'Ctrl+Alt+C', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Code block" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.toggleCodeBlock())}
              aria-pressed={state.codeBlock}
              className={`rte-btn rte-secondary ${state.codeBlock ? 'is-active' : ''}`}><LuCode className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content="Horizontal Rule" className="rte-secondary">
            <button type="button" aria-label="Horizontal rule" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.setHorizontalRule())}
              className="rte-btn rte-secondary"><LuMinus className="rte-icon" /></button>
          </Tooltip>

          <span className="rte-sep rte-secondary" />

          <Tooltip content="Clear Formatting" className="rte-secondary">
            <button type="button" aria-label="Clear formatting" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.unsetAllMarks().clearNodes())}
              className="rte-btn rte-secondary"><LuEraser className="rte-icon" /></button>
          </Tooltip>

          {/* More button — popover on desktop, bottom sheet on mobile */}
          <Tooltip content="More formatting options" className="rte-more-btn">
            <button type="button" aria-label="More formatting options"
              ref={moreBtnRef}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setMoreSheetOpen(o => !o)}
              aria-expanded={moreSheetOpen}
              aria-haspopup="dialog"
              className={`rte-btn ${moreSheetOpen ? 'is-active' : ''}`}><LuEllipsis className="rte-icon" /></button>
          </Tooltip>

          <span className="rte-grow rte-secondary" />

          {/* Save state + count — toolbar position (desktop) */}
          {saveState && (
            <span className={`rte-save-state rte-save-${saveState} rte-secondary`} aria-live="polite">
              {saveState === 'saving' && 'Saving…'}
              {saveState === 'saved' && 'Saved'}
              {saveState === 'error' && 'Save failed'}
            </span>
          )}

          {showCharacterCount && (
            <span className={`rte-count rte-secondary ${overLimit ? 'rte-count-over' : ''}`} aria-live="polite">
              {formatCount(state.words)} {state.words === 1 ? 'word' : 'words'}
              {characterLimit ? ` · ${formatCount(state.characters)} / ${formatCount(characterLimit)}` : ''}
            </span>
          )}

          <Tooltip content={tip('Undo', 'Ctrl+Z', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Undo" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.undo())}
              disabled={!state.canUndo}
              className="rte-btn rte-secondary"><LuUndo className="rte-icon" /></button>
          </Tooltip>

          <Tooltip content={tip('Redo', 'Ctrl+Shift+Z', isTouch)} className="rte-secondary">
            <button type="button" aria-label="Redo" onMouseDown={(e) => e.preventDefault()}
              onClick={cmd(c => c.redo())}
              disabled={!state.canRedo}
              className="rte-btn rte-secondary"><LuRedo className="rte-icon" /></button>
          </Tooltip>
        </div>
      )}

      <EditorContent editor={editor} />

      {/* Mobile status bar — shows save state + count below editor where it's always visible */}
      {showStatusBar && (
        <div className="rte-tt-status" role="status">
          {saveState && (
            <span className={`rte-save-state rte-save-${saveState}`} aria-live="polite">
              {saveState === 'saving' && 'Saving…'}
              {saveState === 'saved' && 'Saved'}
              {saveState === 'error' && 'Save failed'}
            </span>
          )}
          {showCharacterCount && (
            <span className={`rte-count ${overLimit ? 'rte-count-over' : ''}`} aria-live="polite">
              {formatCount(state.words)} {state.words === 1 ? 'word' : 'words'}
              {characterLimit ? ` · ${formatCount(state.characters)} / ${formatCount(characterLimit)}` : ''}
            </span>
          )}
        </div>
      )}

      {/* Hidden file input for image picker */}
      {enableImages && (
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={onImageInputChange}
        />
      )}

      <LinkModal
        isOpen={linkModalOpen}
        onClose={() => {
          setLinkModalOpen(false)
          restoreSelection()
        }}
        onSubmit={(url) => {
          restoreSelection()
          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
          notifyChangeNow(editor)
          setLinkModalOpen(false)
        }}
        onRemove={() => {
          restoreSelection()
          editor.chain().focus().extendMarkRange('link').unsetLink().run()
          notifyChangeNow(editor)
          setLinkModalOpen(false)
        }}
        initialUrl={linkModalInitialUrl}
      />

      <MobileFormatSheet
        isOpen={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        sections={sheetSections}
        anchorRef={moreBtnRef}
      />
    </div>
  )
}

export default function RichTextEditorTipTap(props) {
  return (
    <EditorErrorBoundary>
      <RichTextEditorTipTapInner {...props} />
    </EditorErrorBoundary>
  )
}
